import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Calendar, User, TrendingUp, MessageSquare, FileText,
  CheckCircle2, Send, Upload, Download, Trash2, BarChart3
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { apiFetch } from '../lib/api';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [actions, setActions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [newMessage, setNewMessage] = useState('');
  const [senderName, setSenderName] = useState('Sophie Martin');
  
  // Phase editing
  const [isPhaseDialogOpen, setIsPhaseDialogOpen] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [selectedPhaseIndex, setSelectedPhaseIndex] = useState(null);
  
  // Document upload
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [newDocument, setNewDocument] = useState({
    name: '',
    type: 'Cahier des charges',
    url: '#',
    size: '',
    uploaded_by: 'Sophie Martin'
  });

  useEffect(() => {
    if (projectId) {
      fetchProjectData();
    }
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const [projectRes, actionsRes, messagesRes, documentsRes, metricsRes] = await Promise.all([
        apiFetch(`/api/projects/${projectId}`),
        apiFetch(`/api/actions?project_id=${projectId}`),
        apiFetch(`/api/messages?project_id=${projectId}`),
        apiFetch(`/api/documents?project_id=${projectId}`),
        apiFetch(`/api/metrics?project_id=${projectId}`)
      ]);

      const projectData = await projectRes.json();
      const actionsData = await actionsRes.json();
      const messagesData = await messagesRes.json();
      const documentsData = await documentsRes.json();
      const metricsData = await metricsRes.json();

      setProject(projectData);
      setActions(actionsData);
      setMessages(messagesData);
      setDocuments(documentsData);
      setMetrics(metricsData[0] || null);
    } catch (error) {
      console.error('Error fetching project data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      const response = await apiFetch('/api/messages', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          sender_name: senderName,
          sender_type: 'équipe',
          content: newMessage
        })
      });

      if (response.ok) {
        setNewMessage('');
        await fetchProjectData();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handlePhaseClick = (phase, index) => {
    setSelectedPhase({ ...phase });
    setSelectedPhaseIndex(index);
    setIsPhaseDialogOpen(true);
  };

  const handleUpdatePhase = async () => {
    if (!selectedPhase || selectedPhaseIndex === null) return;

    try {
      const updatedPhases = [...project.phases];
      updatedPhases[selectedPhaseIndex] = selectedPhase;

      const response = await apiFetch(`/api/projects/${projectId}`, {
        method: 'PUT',
        body: JSON.stringify({ phases: updatedPhases })
      });

      if (response.ok) {
        await fetchProjectData();
        setIsPhaseDialogOpen(false);
        setSelectedPhase(null);
        setSelectedPhaseIndex(null);
      }
    } catch (error) {
      console.error('Error updating phase:', error);
    }
  };

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleAddDocument = async () => {
    if (!selectedFile) {
      alert('Veuillez sélectionner un fichier');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', newDocument.type);
      formData.append('uploaded_by', newDocument.uploaded_by);

      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      });

      xhr.onload = async () => {
        if (xhr.status === 200) {
          await fetchProjectData();
          setIsDocumentDialogOpen(false);
          setSelectedFile(null);
          setNewDocument({
            name: '',
            type: 'Cahier des charges',
            url: '#',
            size: '',
            uploaded_by: 'Sophie Martin'
          });
        } else {
          alert('Erreur lors du téléchargement du fichier');
        }
        setIsUploading(false);
        setUploadProgress(0);
      };

      xhr.onerror = () => {
        alert('Erreur lors du téléchargement du fichier');
        setIsUploading(false);
        setUploadProgress(0);
      };

      xhr.open('POST', `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/upload`);
      // Add user auth header for data isolation
      const user = JSON.parse(localStorage.getItem('currentUser') || 'null');
      if (user?.id) {
        xhr.setRequestHeader('X-User-Id', user.id);
      }
      xhr.send(formData);
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Erreur lors du téléchargement du fichier');
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileSelect = (file) => {
    if (file) {
      setSelectedFile(file);
      setNewDocument({
        ...newDocument,
        name: file.name
      });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Supprimer ce document ?')) return;

    try {
      const response = await apiFetch(`/api/documents/${documentId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchProjectData();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'Excellent': return 'text-green-600 bg-green-50 border-green-200';
      case 'Bon': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'À risque': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'Critique': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPhaseColor = (status) => {
    switch (status) {
      case 'Terminé': return 'bg-green-500';
      case 'En cours': return 'bg-blue-500';
      case 'En attente client': return 'bg-orange-500';
      case 'Non démarré': return 'bg-gray-300';
      default: return 'bg-gray-300';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'À faire': return 'bg-gray-100 text-gray-700';
      case 'En cours': return 'bg-blue-100 text-blue-700';
      case 'En attente client': return 'bg-orange-100 text-orange-700';
      case 'En validation': return 'bg-purple-100 text-purple-700';
      case 'Terminé': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="text-muted-foreground mb-4">Projet non trouvé</div>
        <Button onClick={() => navigate('/')}>Retour au tableau de bord</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        className="mb-4"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour
      </Button>

      {/* Project Header */}
      <Card className="p-6" data-testid="project-header">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                {project.client_name}
              </h1>
              <Badge variant="outline" className="text-base">
                {project.project_type}
              </Badge>
            </div>
            <p className="text-base text-muted-foreground mb-4">
              {project.description}
            </p>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <span>Chef de projet: <strong>{project.project_manager}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span>Début: <strong>{project.start_date}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span>Livraison: <strong>{project.estimated_delivery}</strong></span>
              </div>
              {project.budget && (
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-muted-foreground" />
                  <span>Budget: <strong>{project.budget.toLocaleString()}€</strong></span>
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            <Badge className={`${getHealthColor(project.health_score)} text-lg px-4 py-2 mb-2`}>
              {project.health_score}
            </Badge>
            <div className="text-4xl font-bold text-primary">{project.progress}%</div>
            <div className="text-sm text-muted-foreground">Progression</div>
          </div>
        </div>
        <Progress value={project.progress} className="h-3" />
      </Card>

      {/* Timeline des phases */}
      <Card className="p-6" data-testid="project-timeline">
        <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5" />
          Timeline du projet
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {project.phases?.map((phase, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              onClick={() => handlePhaseClick(phase, idx)}
              className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
              data-testid={`phase-${idx}`}
            >
              <div className={`w-3 h-3 rounded-full ${getPhaseColor(phase.status)}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{phase.name}</div>
                <div className="text-xs text-muted-foreground">{phase.status}</div>
              </div>
              {phase.progress > 0 && (
                <div className="text-sm font-medium text-muted-foreground">
                  {phase.progress}%
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="actions" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="actions">Actions ({actions.length})</TabsTrigger>
          <TabsTrigger value="communication">Communication ({messages.length})</TabsTrigger>
          <TabsTrigger value="documents">Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
        </TabsList>

        {/* Actions Tab */}
        <TabsContent value="actions" className="mt-6">
          <Card data-testid="actions-tab">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Phase</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Responsable</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Date limite</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {actions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Aucune action pour ce projet
                    </TableCell>
                  </TableRow>
                ) : (
                  actions.map((action, idx) => (
                    <TableRow key={action.id} data-testid={`action-${idx}`}>
                      <TableCell>
                        <Badge variant="outline">{action.phase}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">{action.title}</TableCell>
                      <TableCell>{action.assignee}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(action.status)}>
                          {action.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{action.due_date}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Communication Tab */}
        <TabsContent value="communication" className="mt-6">
          <Card className="p-6" data-testid="communication-tab">
            <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Aucun message
                </div>
              ) : (
                messages.map((message, idx) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`flex gap-3 ${message.sender_type === 'équipe' ? 'justify-start' : 'justify-end'}`}
                    data-testid={`message-${idx}`}
                  >
                    <div className={`max-w-[70%] p-4 rounded-lg ${
                      message.sender_type === 'équipe'
                        ? 'bg-blue-50 border border-blue-200'
                        : 'bg-green-50 border border-green-200'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium">{message.sender_name}</span>
                        <Badge variant="outline" className="text-xs">
                          {message.sender_type}
                        </Badge>
                      </div>
                      <p className="text-sm">{message.content}</p>
                      <div className="text-xs text-muted-foreground mt-2">
                        {new Date(message.timestamp).toLocaleString('fr-FR')}
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Send Message */}
            <div className="border-t pt-4">
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Votre nom"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  className="w-48"
                />
              </div>
              <div className="flex gap-2">
                <Textarea
                  placeholder="Écrire un message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  rows={3}
                  className="flex-1"
                  data-testid="message-input"
                />
                <Button onClick={handleSendMessage} data-testid="send-message-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-6">
          <Card className="p-6" data-testid="documents-tab">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold">Documents du projet</h3>
              <Button onClick={() => setIsDocumentDialogOpen(true)} size="sm">
                <Upload className="w-4 h-4 mr-2" />
                Ajouter un document
              </Button>
            </div>
            
            {documents.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Aucun document</h3>
                <p className="text-muted-foreground mb-4">
                  Commencez par ajouter des documents au projet
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc, idx) => (
                  <motion.div
                    key={doc.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    data-testid={`document-${idx}`}
                  >
                    <FileText className="w-8 h-8 text-primary" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium truncate">{doc.name}</h3>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <Badge variant="outline" className="text-xs">{doc.type}</Badge>
                        <span>•</span>
                        <span>{doc.size}</span>
                        <span>•</span>
                        <span>Par {doc.uploaded_by}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          if (doc.url && doc.url !== '#') {
                            const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}${doc.url}`;
                            const link = document.createElement('a');
                            link.href = downloadUrl;
                            link.download = doc.name;
                            link.target = '_blank';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }
                        }}
                        disabled={!doc.url || doc.url === '#'}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteDocument(doc.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="mt-6">
          <Card className="p-6" data-testid="performance-tab">
            {metrics ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg border bg-card">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="w-4 h-4 text-primary" />
                    <span className="text-sm text-muted-foreground">Trafic</span>
                  </div>
                  <div className="text-2xl font-bold">{metrics.traffic?.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground mt-1">visiteurs/mois</div>
                </div>

                <div className="p-4 rounded-lg border bg-card">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <span className="text-sm text-muted-foreground">Leads générés</span>
                  </div>
                  <div className="text-2xl font-bold">{metrics.leads}</div>
                  <div className="text-xs text-muted-foreground mt-1">contacts qualifiés</div>
                </div>

                <div className="p-4 rounded-lg border bg-card">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <span className="text-sm text-muted-foreground">Taux de conversion</span>
                  </div>
                  <div className="text-2xl font-bold">{metrics.conversion_rate}%</div>
                  <div className="text-xs text-muted-foreground mt-1">visiteurs → leads</div>
                </div>

                <div className="p-4 rounded-lg border bg-card">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="w-4 h-4 text-primary" />
                    <span className="text-sm text-muted-foreground">Score SEO</span>
                  </div>
                  <div className="text-2xl font-bold">{metrics.seo_score}/100</div>
                  <div className="text-xs text-muted-foreground mt-1">optimisation</div>
                </div>

                <div className="p-4 rounded-lg border bg-card">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <span className="text-sm text-muted-foreground">Vitesse du site</span>
                  </div>
                  <div className="text-2xl font-bold">{metrics.page_speed}/100</div>
                  <div className="text-xs text-muted-foreground mt-1">performance</div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Aucune métrique disponible</h3>
                <p className="text-muted-foreground">
                  Les métriques de performance seront disponibles après la mise en ligne
                </p>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Phase Edit Dialog */}
      <Dialog open={isPhaseDialogOpen} onOpenChange={setIsPhaseDialogOpen}>
        <DialogContent data-testid="phase-edit-dialog">
          <DialogHeader>
            <DialogTitle>Modifier la phase</DialogTitle>
            <DialogDescription>
              Mettez à jour le statut et la progression de la phase
            </DialogDescription>
          </DialogHeader>
          
          {selectedPhase && (
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Phase</label>
                <Input value={selectedPhase.name} disabled />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Statut</label>
                <Select
                  value={selectedPhase.status}
                  onValueChange={(value) => setSelectedPhase({ ...selectedPhase, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Non démarré">Non démarré</SelectItem>
                    <SelectItem value="En cours">En cours</SelectItem>
                    <SelectItem value="En attente client">En attente client</SelectItem>
                    <SelectItem value="Terminé">Terminé</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Progression (%)</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={selectedPhase.progress}
                  onChange={(e) => setSelectedPhase({ ...selectedPhase, progress: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Date de début</label>
                  <Input
                    type="date"
                    value={selectedPhase.start_date || ''}
                    onChange={(e) => setSelectedPhase({ ...selectedPhase, start_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Date de fin</label>
                  <Input
                    type="date"
                    value={selectedPhase.end_date || ''}
                    onChange={(e) => setSelectedPhase({ ...selectedPhase, end_date: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPhaseDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdatePhase}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Document Dialog */}
      <Dialog open={isDocumentDialogOpen} onOpenChange={setIsDocumentDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="add-document-dialog">
          <DialogHeader>
            <DialogTitle>Ajouter un document</DialogTitle>
            <DialogDescription>
              Glissez-déposez un fichier ou cliquez pour sélectionner
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {/* Drag & Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
                ${isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}
              `}
              onClick={() => document.getElementById('file-input').click()}
            >
              <input
                id="file-input"
                type="file"
                className="hidden"
                onChange={handleFileInputChange}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.zip,.rar"
              />
              
              {selectedFile ? (
                <div className="space-y-2">
                  <FileText className="w-12 h-12 mx-auto text-primary" />
                  <div>
                    <p className="font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setNewDocument({ ...newDocument, name: '' });
                    }}
                  >
                    Changer de fichier
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="w-12 h-12 mx-auto text-muted-foreground" />
                  <div>
                    <p className="font-medium">Glissez-déposez un fichier ici</p>
                    <p className="text-sm text-muted-foreground">
                      ou cliquez pour sélectionner un fichier
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    PDF, DOC, XLS, PPT, Images, ZIP (max 50 MB)
                  </p>
                </div>
              )}
            </div>

            {selectedFile && (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Type de document</label>
                  <Select
                    value={newDocument.type}
                    onValueChange={(value) => setNewDocument({ ...newDocument, type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cahier des charges">Cahier des charges</SelectItem>
                      <SelectItem value="Maquettes">Maquettes</SelectItem>
                      <SelectItem value="Contrats">Contrats</SelectItem>
                      <SelectItem value="Livrables">Livrables</SelectItem>
                      <SelectItem value="Contenus">Contenus</SelectItem>
                      <SelectItem value="Autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Uploadé par</label>
                  <Input
                    value={newDocument.uploaded_by}
                    onChange={(e) => setNewDocument({ ...newDocument, uploaded_by: e.target.value })}
                    placeholder="Nom de la personne"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsDocumentDialogOpen(false);
              setSelectedFile(null);
              setNewDocument({
                name: '',
                type: 'Cahier des charges',
                url: '#',
                size: '',
                uploaded_by: 'Sophie Martin'
              });
            }} disabled={isUploading}>
              Annuler
            </Button>
            <Button onClick={handleAddDocument} disabled={!selectedFile || isUploading}>
              {isUploading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Téléchargement... {uploadProgress}%
                </span>
              ) : (
                'Ajouter le document'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;
